import { useState, useEffect } from 'react';
import { BusinessIdea, ComparisonAnalysis } from '@/src/types';
import { sanitizeData } from '@/src/utils/formatters';
import { db, onSnapshot, collection, handleFirestoreError, OperationType } from '@/firebase';
import { compareOpportunities } from '@/src/services/apiServices';
import { useAuth } from '@/src/contexts/AuthContext';

export interface LoadingStates {
  isFetchingSaved: boolean;
  isComparing: boolean;
}

export const useVentureIntelligence = () => {
  const { isLoggedIn, user, isDemoGuest } = useAuth();
  const [savedVentures, setSavedVentures] = useState<BusinessIdea[]>([]);
  const [comparisonData, setComparisonData] = useState<ComparisonAnalysis | null>(null);
  const [loadingStates, setLoadingStates] = useState<LoadingStates>({
    isFetchingSaved: false,
    isComparing: false
  });

  useEffect(() => {
    if (!isLoggedIn || !user) {
      setSavedVentures([]);
      return;
    }

    if (isDemoGuest) {
      const loadGuestIdeas = () => {
        try {
          const saved = localStorage.getItem('demo_guest_saved_ideas');
          const parsed = saved ? JSON.parse(saved) : [];
          setSavedVentures(sanitizeData(parsed) as BusinessIdea[]);
        } catch (err) {
          console.warn('Error reading demo guest saved ideas:', err);
          setSavedVentures([]);
        }
      };
      
      loadGuestIdeas();
      
      window.addEventListener('demo-guest-ideas-updated', loadGuestIdeas);
      return () => {
        window.removeEventListener('demo-guest-ideas-updated', loadGuestIdeas);
      };
    }

    setLoadingStates(prev => ({ ...prev, isFetchingSaved: true }));
    const userId = user.uid;
    const ideasUnsubscribe = onSnapshot(collection(db, 'users', userId, 'savedIdeas'), (snapshot) => {
      const ideas = snapshot.docs.map(doc => {
        const data = doc.data() as BusinessIdea;
        return { 
          ...data, 
          id: doc.id
        };
      });
      setSavedVentures(sanitizeData(ideas) as BusinessIdea[]);
      setLoadingStates(prev => ({ ...prev, isFetchingSaved: false }));
    }, (e) => {
      handleFirestoreError(e, OperationType.GET, `users/${userId}/savedIdeas`);
      setLoadingStates(prev => ({ ...prev, isFetchingSaved: false }));
    });

    return () => ideasUnsubscribe();
  }, [isLoggedIn, user, isDemoGuest]);

  const runComparison = async (activeIdeas: BusinessIdea[], preferredCurrency: string): Promise<ComparisonAnalysis | undefined> => {
    if (activeIdeas.length < 2) return undefined;
    setLoadingStates(prev => ({ ...prev, isComparing: true }));
    try {
      const result = await compareOpportunities(activeIdeas, preferredCurrency) as ComparisonAnalysis;
      setComparisonData(result);
      return result;
    } catch (error) {
      console.error('Comparison error:', error);
      throw error;
    } finally {
      setLoadingStates(prev => ({ ...prev, isComparing: false }));
    }
  };
  
  return {
    savedVentures,
    comparisonData,
    loadingStates,
    runComparison,
    setComparisonData
  };
};

export default useVentureIntelligence;
